import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { projects } from '../data/portfolio';
import { ExternalLink, Github, ChevronDown, ChevronUp, Star, GitFork } from 'lucide-react';
import ParticleField from './ParticleField';
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

const colorMap = {
  primary: { badge: 'border-primary/30 text-primary bg-primary/5', accent: 'hsl(var(--primary))', glow: 'hsl(var(--primary) / 0.2)' },
  secondary: { badge: 'border-secondary/30 text-secondary bg-secondary/5', accent: 'hsl(var(--secondary))', glow: 'hsl(var(--secondary) / 0.15)' },
  accent: { badge: 'border-accent/30 text-accent bg-accent/5', accent: 'hsl(var(--accent))', glow: 'hsl(var(--accent) / 0.2)' }
};

const ProjectCard = ({ project, index }) => {
  const [expanded, setExpanded] = useState(false);
  const c = colorMap[project.color];

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      custom={index}
      viewport={{ once: true }}
      className="glass glass-hover rounded-2xl overflow-hidden flex flex-col"
      style={{ '--glow': c.glow }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-500"
          style={{ filter: 'brightness(0.4) saturate(0.8)' }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(180deg, transparent 0%, hsl(var(--card)) 90%)` }}
        />
        {/* Featured tag */}
        {project.featured && (
          <div className="absolute top-3 left-3">
            <Badge className={`${c.badge} border text-xs font-mono`}>
              Featured
            </Badge>
          </div>
        )}
        {/* Live indicator */}
        {project.liveUrl && project.liveUrl !== '#' && (
          <div className="absolute top-3 right-3">
            <span className="flex items-center gap-1.5 text-xs font-mono text-accent">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Live
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <div className="mb-3">
          <p className="text-muted-foreground font-mono text-xs mb-1">{project.subtitle}</p>
          <h3 className="font-heading font-bold text-foreground text-xl">{project.title}</h3>
        </div>

        <p className="text-muted-foreground text-sm leading-relaxed mb-4">
          {project.description}
        </p>

        {/* Stats */}
        {project.stats && (
          <div className="flex flex-wrap gap-3 mb-4">
            {Object.entries(project.stats).map(([key, val]) => (
              <div key={key} className="text-xs">
                <span className="font-mono font-bold" style={{ color: c.accent }}>{val}</span>
                <span className="text-muted-foreground ml-1 capitalize">{key}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tech tags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {project.tech.map((t) => (
            <span
              key={t}
              className="font-mono text-xs px-2 py-0.5 rounded-md border border-border/60 text-muted-foreground"
            >
              {t}
            </span>
          ))}
        </div>

        {/* Expandable deep dive */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mb-4"
            >
              <div
                className="p-4 rounded-xl text-sm text-muted-foreground leading-relaxed"
                style={{ background: 'hsl(var(--muted) / 0.5)', borderLeft: `2px solid ${c.accent}` }}
              >
                <p className="font-mono text-xs mb-2" style={{ color: c.accent }}>// Technical Deep Dive</p>
                {project.longDescription}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-auto pt-2">
          {project.liveUrl && project.liveUrl !== '#' && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/60"
              asChild
            >
              <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} className="mr-1.5" />
                Demo
              </a>
            </Button>
          )}
          {project.githubUrl && project.githubUrl !== '#' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              asChild
            >
              <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                <Github size={14} className="mr-1.5" />
                Code
              </a>
            </Button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors duration-200"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Less' : 'Deep Dive'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ProjectsSection = () => {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? projects : projects.filter(p => p.featured);

  return (
    <section id="projects" className="relative py-24 px-6 md:px-12 lg:px-24">
      <div
        className="absolute right-0 top-0 w-[400px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.04) 0%, transparent 70%)' }}
      />
      <ParticleField />
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Projects
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Featured
            <span className="gradient-text"> Work</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl">
            AI systems, developer tools, and intelligent applications built for real-world impact.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>

        {!showAll && projects.length > 3 && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              variant="outline"
              size="lg"
              className="border-border hover:border-primary/50 text-foreground hover:text-primary hover:bg-primary/5"
              onClick={() => setShowAll(true)}
            >
              View All Projects
              <ChevronDown size={16} className="ml-2" />
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ProjectsSection;
